import { INTERVAL_DAYS, findTerm, schedule, type Grade } from '@engpod/core';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../components/Icon';
import { PageHead } from '../components/PageHead';
import { api, errorText } from '../lib/api';
import { useAuth } from '../lib/auth';
import { playClip, stopClip } from '../lib/clip';
import { prefs } from '../lib/local';
import type { VocabItem } from '../types';

interface Stats {
  total: number;
  due: number;
  mastered: number;
  reviewedToday: number;
  nextDueAt: number | null;
}
type Phase = 'start' | 'session' | 'done';
const BATCH = 20;

function untilText(at: number | null): string {
  if (!at) return '';
  const ms = at - Date.now();
  if (ms < 3_600_000) return `${Math.max(1, Math.round(ms / 60_000))} 分钟后`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)} 小时后`;
  return `${Math.round(ms / 86_400_000)} 天后`;
}

export function ReviewPage() {
  const { me } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [phase, setPhase] = useState<Phase>('start');
  const [queue, setQueue] = useState<VocabItem[]>([]);
  const [pos, setPos] = useState(0);
  const [tally, setTally] = useState<Record<Grade, number>>({ again: 0, hard: 0, good: 0 });
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(() => api<Stats>('/review/stats').then(setStats, (e) => setError(errorText(e))), []);
  useEffect(() => {
    if (me?.user) void loadStats();
  }, [me?.user, loadStats]);

  const start = async () => {
    try {
      const { items } = await api<{ items: VocabItem[] }>(`/review/due?limit=${BATCH}`);
      setQueue(items);
      setPos(0);
      setTally({ again: 0, hard: 0, good: 0 });
      setPhase(items.length ? 'session' : 'done');
    } catch (e) {
      setError(errorText(e));
    }
  };

  const grade = async (g: Grade) => {
    const item = queue[pos];
    if (!item) return;
    stopClip();
    await api(`/review/${item.id}`, { method: 'POST', json: { grade: g } });
    setTally((t) => ({ ...t, [g]: t[g] + 1 }));
    window.dispatchEvent(new Event('engpod:review-changed'));
    // "Forgot" comes back at the end of this round, so the session ends on a remembered card.
    if (g === 'again') setQueue((q) => [...q, { ...item, ...schedule({ stage: item.stage, dueAt: item.dueAt, reviews: item.reviews }, 'again') }]);
    if (pos + 1 >= queue.length + (g === 'again' ? 1 : 0)) {
      setPhase('done');
      void loadStats();
    } else setPos((p) => p + 1);
  };

  if (!me) return <p className="page muted">加载中…</p>;

  if (phase === 'session') {
    const item = queue[pos];
    if (!item) return null;
    return <Flashcard key={`${item.id}-${pos}`} item={item} pos={pos} total={queue.length} onGrade={grade} onExit={() => (stopClip(), setPhase('done'), void loadStats())} />;
  }

  const goal = prefs.get<number>('reviewGoal', 20);
  const today = stats?.reviewedToday ?? 0;
  const due = stats?.due ?? 0;
  const pct = Math.min(1, today / Math.max(1, goal));
  const r = 44;
  const c = 2 * Math.PI * r;
  const reviewedNow = tally.again + tally.hard + tally.good;

  return (
    <div className="page review">
      <PageHead eyebrow="SPACED REVIEW" title="复习。" sub="先听原句，想一想，再翻面。" />
      {error && <p className="status">{error}</p>}

      {phase === 'done' && reviewedNow > 0 && (
        <section className="result-card">
          <div className="eyebrow light">本轮完成</div>
          <h2>复习了 {reviewedNow} 次</h2>
          <div className="result-bars">
            <span className="good">记住了 {tally.good}</span>
            <span className="hard">模糊 {tally.hard}</span>
            <span className="again">忘了 {tally.again}</span>
          </div>
          <p>记住的会按 1 / 3 / 7 / 14 / 30 / 60 天的间隔再出现，越来越牢。</p>
        </section>
      )}

      <section className="review-hero">
        <svg className="ring big" viewBox="0 0 104 104" aria-hidden="true">
          <circle cx="52" cy="52" r={r} className="ring-bg" />
          <circle cx="52" cy="52" r={r} className="ring-fg" strokeDasharray={`${c * pct} ${c}`} />
        </svg>
        <div className="hero-center">
          <b>{today}</b>
          <small>/ {goal} 今日</small>
        </div>
        <div className="hero-text">
          {due > 0 ? (
            <>
              <h2>{due} 条待复习</h2>
              <p className="muted">每轮最多 {BATCH} 条，大约 {Math.max(1, Math.round(Math.min(due, BATCH) * 0.2))} 分钟</p>
            </>
          ) : (
            <>
              <h2>{stats?.total ? '都复习完了' : '还没有需要复习的'}</h2>
              <p className="muted">{stats?.nextDueAt ? `下一条 ${untilText(stats.nextDueAt)}到期` : '精听时把生词加入生词本，第二天就会出现在这里。'}</p>
            </>
          )}
        </div>
      </section>

      {due > 0 ? (
        <button className="primary big-start" onClick={start}>
          {phase === 'done' && reviewedNow > 0 ? '再来一轮' : '开始复习'} · {Math.min(due, BATCH)} 条
        </button>
      ) : (
        <Link className="button primary big-start" to={stats?.total ? '/vocab' : '/'}>
          {stats?.total ? '看看生词本' : '去精听'}
        </Link>
      )}

      <section className="me-card review-facts">
        <div>
          <b>{stats?.total ?? 0}</b>
          <span>生词本</span>
        </div>
        <div>
          <b>{stats?.mastered ?? 0}</b>
          <span>已掌握</span>
        </div>
        <div>
          <b>{today}</b>
          <span>今天复习</span>
        </div>
      </section>

      <section className="me-card how-it-works">
        <h3>复习怎么安排</h3>
        <div className="interval-line">
          {INTERVAL_DAYS.map((d) => (
            <span key={d}>{d} 天</span>
          ))}
        </div>
        <p className="muted">记住了：间隔逐级拉长。模糊：明天再来，间隔退一级。忘了：10 分钟后在本轮末尾重来。</p>
      </section>
    </div>
  );
}

function Flashcard({ item, pos, total, onGrade, onExit }: { item: VocabItem; pos: number; total: number; onGrade(g: Grade): void; onExit(): void }) {
  const [shown, setShown] = useState(false);
  const [busy, setBusy] = useState(false);
  // Meet the word in a different sentence each time it comes back.
  const ctx = item.contexts[(item.reviews + pos) % Math.max(1, item.contexts.length)];
  useEffect(() => {
    if (ctx) void playClip(ctx);
    return stopClip;
  }, [ctx]);
  if (!ctx) return null;
  const at = findTerm(ctx.en, item.surface);
  const next = INTERVAL_DAYS[Math.min(item.stage, INTERVAL_DAYS.length - 1)];
  const choose = async (g: Grade) => {
    setBusy(true);
    await onGrade(g);
  };

  return (
    <div className="page review session">
      <div className="session-top">
        <button className="plain" onClick={onExit} aria-label="结束本轮">
          <Icon name="close" />
        </button>
        <div className="session-progress">
          <i style={{ width: `${(pos / total) * 100}%` }} />
        </div>
        <small className="muted">
          {pos + 1}/{total}
        </small>
      </div>

      <article className={`flashcard${shown ? ' flipped' : ''}`}>
        <div className="card-source">
          {ctx.programTitle ?? '我的资料'} · {ctx.episodeTitle}
        </div>
        <button className="listen-button" onClick={() => void playClip(ctx)} aria-label="再听一遍原句">
          <Icon name="volume" />
        </button>
        <p className="card-sentence">
          {at && !shown ? (
            <>
              {ctx.en.slice(0, at.start)}
              <span className="blank">{'＿'.repeat(Math.min(8, Math.max(3, Math.ceil((at.end - at.start) / 2))))}</span>
              {ctx.en.slice(at.end)}
            </>
          ) : at ? (
            <>
              {ctx.en.slice(0, at.start)}
              <mark>{ctx.en.slice(at.start, at.end)}</mark>
              {ctx.en.slice(at.end)}
            </>
          ) : (
            ctx.en
          )}
        </p>
        {!shown && item.meaning && <p className="card-hint">提示：{item.meaning}</p>}
        {shown && (
          <div className="card-answer">
            <h2>{item.surface}</h2>
            {item.meaning && <p className="card-meaning">{item.meaning}</p>}
            {ctx.zh && <p className="card-zh">{ctx.zh}</p>}
            {item.note && <p className="word-note">✎ {item.note}</p>}
          </div>
        )}
      </article>

      {shown ? (
        <div className="grade-row">
          <button className="grade again" disabled={busy} onClick={() => choose('again')}>
            忘了<small>10 分钟</small>
          </button>
          <button className="grade hard" disabled={busy} onClick={() => choose('hard')}>
            模糊<small>1 天</small>
          </button>
          <button className="grade good" disabled={busy} onClick={() => choose('good')}>
            记住了<small>{next} 天</small>
          </button>
        </div>
      ) : (
        <button className="primary big-start" onClick={() => setShown(true)}>
          显示答案
        </button>
      )}
    </div>
  );
}
